import { type Map as MapboxMap, type MapMouseEvent, Popup } from 'mapbox-gl';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { registerMapEvent } from '@/libs/utils/registerMapEvents';
import { LayerId } from '../constants';
import { readMvtNumber } from '../utils/readMvtNumber';

/** What the popup renders. Same property names from either station source. */
export interface StationPopupData {
  cpo: string;
  charges?: number;
  operativeHpcSpots?: number;
  lngLat: { lat: number; lng: number };
}

type StationFeature = NonNullable<MapMouseEvent['features']>[number];

function stationKey(feature: StationFeature): string {
  const geometry = feature.geometry;
  return geometry?.type === 'Point'
    ? geometry.coordinates.join(',')
    : JSON.stringify(feature.properties);
}

function popupDataFrom(event: MapMouseEvent, feature: StationFeature): StationPopupData {
  const properties = feature.properties as {
    cpo?: string;
    charges_per_station_per_day?: unknown;
    operative_hpc_spots?: unknown;
  };

  return {
    cpo: properties.cpo ?? '',
    charges: readMvtNumber(properties.charges_per_station_per_day),
    operativeHpcSpots: readMvtNumber(properties.operative_hpc_spots),
    lngLat: { lat: event.lngLat.lat, lng: event.lngLat.lng },
  };
}

export interface StationsInteractionsOptions {
  /** One overlay may be painted as several layers. */
  layerIds: LayerId[];
  /** Set while a map tool is armed, whose click must not also open a popup. */
  suppressRef?: RefObject<boolean>;
}

/**
 * Click-to-inspect for the competitor-station tiles, following `useZonePopup`:
 * the caller portals `StationMapPopup` into `popupContainer`, which is non-null
 * exactly when `selectedStation` is.
 */
export function useStationsInteractions(
  map: RefObject<MapboxMap | null>,
  options: StationsInteractionsOptions,
) {
  const { layerIds, suppressRef } = options;

  const [selectedStation, setSelectedStation] = useState<StationPopupData | null>(null);
  // State, not a ref: the caller's portal only mounts if publishing re-renders.
  const [popupContainer, setPopupContainer] = useState<HTMLDivElement | null>(null);

  // 'preclick' closes the popup before the layer 'click' runs, so telling a
  // re-click from a new one needs the key snapshotted there.
  const openKeyRef = useRef<string | null>(null);
  const preclickKeyRef = useRef<string | null>(null);

  function handlePreclick() {
    preclickKeyRef.current = openKeyRef.current;
    openKeyRef.current = null;
    setSelectedStation(null);
  }

  function handleMouseClick(event: MapMouseEvent) {
    const feature = event.features?.[0];
    if (!feature) return;

    if (suppressRef?.current) return;

    const key = stationKey(feature);
    const wasOpenKey = preclickKeyRef.current;
    preclickKeyRef.current = null;
    // Re-clicking the open station closes it, which preclick already did.
    if (wasOpenKey === key) return;

    openKeyRef.current = key;
    setSelectedStation(popupDataFrom(event, feature));
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key !== 'Escape') return;
    openKeyRef.current = null;
    setSelectedStation(null);
  }

  // `closeOnClick` stays off: preclick owns closing and must run for every
  // click, including those landing on no station.
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; react to the selection only
  useEffect(() => {
    const currentMap = map.current;
    if (!selectedStation || !currentMap) {
      setPopupContainer(null);
      return;
    }

    const container = document.createElement('div');
    const popup = new Popup({
      closeButton: false,
      closeOnClick: false,
      closeOnMove: true,
      maxWidth: 'none',
      className: 'vanilla-popup',
    });

    popup
      .setDOMContent(container)
      .setLngLat([selectedStation.lngLat.lng, selectedStation.lngLat.lat])
      .addTo(currentMap);
    setPopupContainer(container);

    // closeOnMove closes without a click, so preclick never runs.
    let removed = false;
    popup.on('close', () => {
      if (removed) return;
      removed = true;
      openKeyRef.current = null;
      setSelectedStation(null);
    });

    return () => {
      removed = true;
      popup.remove();
    };
  }, [selectedStation]);

  function setupStationsInteractions() {
    const currentMap = map.current;
    if (!currentMap) return () => {};

    const cleanupPreclick = registerMapEvent(currentMap, 'preclick', handlePreclick);
    const cleanupClicks = layerIds.map((layerId) =>
      registerMapEvent(currentMap, 'click', layerId, handleMouseClick),
    );
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      openKeyRef.current = null;
      setSelectedStation(null);
      for (const cleanupClick of cleanupClicks) cleanupClick();
      cleanupPreclick();
    };
  }

  return { setupStationsInteractions, selectedStation, popupContainer };
}
